import React, { useMemo } from "react";
import { Box, IconButton, Stack, CssBaseline, useMediaQuery, Tooltip } from "@mui/material";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import { Link as RouterLink, Outlet, useLocation } from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import { useAppColorMode } from "../hooks/colorMode";

const NAV_ITEMS = [
  {
    label: "Login",
    icon: "logo",
    to: "/",
  },
  {
    label: "About",
    icon: <InfoOutlinedIcon fontSize="small" />,
    to: "/about",
  },
  {
    label: "FAQ",
    icon: <QuizOutlinedIcon fontSize="small" />,
    to: "/faq",
  },
  {
    label: "Releases",
    icon: <UpdateOutlinedIcon fontSize="small" />,
    to: "/releases",
  },
];

const PublicLayout = () => {
  const isLandscape = useMediaQuery("(orientation: landscape)");
  const isWide = useMediaQuery("(min-width:900px)");
  const useLeftRail = isLandscape || isWide;
  const { colorMode, toggleColorMode } = useAppColorMode();
  const location = useLocation();

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: colorMode,
          primary: {
            main: colorMode === "dark" ? "#9fb7ff" : "#54719d",
          },
          background: {
            default: colorMode === "dark" ? "#090b12" : "#f7f8fb",
            paper: colorMode === "dark" ? "#101521" : "#ffffff",
          },
        },
        typography: {
          fontSize: 12,
          fontFamily: ["Montserrat", "sans-serif"].join(","),
        },
      }),
    [colorMode]
  );

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }

    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const getButtonStyles = (path) => ({
    width: 44,
    height: 44,
    borderRadius: 3,
    color: isActive(path) ? theme.palette.primary.main : theme.palette.text.secondary,
    bgcolor: isActive(path)
      ? alpha(theme.palette.primary.main, colorMode === "dark" ? 0.2 : 0.12)
      : "transparent",
    border: `1px solid ${
      isActive(path)
        ? alpha(theme.palette.primary.main, 0.28)
        : alpha(theme.palette.text.primary, colorMode === "dark" ? 0.2 : 0.1)
    }`,
    transition: "all 160ms ease",
    "&:hover": {
      color: theme.palette.primary.main,
      bgcolor: alpha(theme.palette.primary.main, colorMode === "dark" ? 0.24 : 0.14),
      transform: "translateY(-1px)",
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
          width: "100vw",
          overflowX: "hidden",
          color: theme.palette.text.primary,
        }}
      >
        <Box
          sx={{
            position: "fixed",
            zIndex: theme.zIndex.drawer + 1,
            left: 0,
            ...(useLeftRail
              ? {
                  top: 0,
                  bottom: 0,
                  width: 84,
                  borderRight: `1px solid ${alpha(
                    theme.palette.text.primary,
                    colorMode === "dark" ? 0.22 : 0.1
                  )}`,
                }
              : {
                  right: 0,
                  bottom: 0,
                  height: 76,
                  borderTop: `1px solid ${alpha(
                    theme.palette.text.primary,
                    colorMode === "dark" ? 0.22 : 0.1
                  )}`,
                }),
            backgroundColor: alpha(
              theme.palette.background.paper,
              colorMode === "dark" ? 0.9 : 0.86
            ),
            backdropFilter: "blur(14px)",
            boxShadow: useLeftRail
              ? "12px 0 30px rgba(15, 23, 42, 0.12)"
              : "0 -12px 30px rgba(15, 23, 42, 0.12)",
          }}
        >
          <Stack
            direction={useLeftRail ? "column" : "row"}
            spacing={useLeftRail ? 1.5 : 0.8}
            alignItems="center"
            justifyContent={useLeftRail ? "center" : "space-around"}
            sx={{
              width: "100%",
              height: "100%",
              py: useLeftRail ? 2 : 0.5,
            }}
          >
            {NAV_ITEMS.map((item) => (
              <Tooltip key={item.label} title={item.label} placement={useLeftRail ? "right" : "top"} arrow>
                <IconButton component={RouterLink} to={item.to} sx={getButtonStyles(item.to)}>
                  {item.icon === "logo" ? (
                    <Box
                      component="img"
                      src="assets/logos/SmartLog.png"
                      alt="SmartLog"
                      sx={{ width: 24, height: 24, objectFit: "contain" }}
                    />
                  ) : (
                    item.icon
                  )}
                </IconButton>
              </Tooltip>
            ))}

            <Tooltip
              title={colorMode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              placement={useLeftRail ? "right" : "top"}
              arrow
            >
              <IconButton onClick={toggleColorMode} sx={getButtonStyles("_mode-toggle")}> 
                {colorMode === "dark" ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        <Box
          sx={{
            minHeight: "100vh",
            pl: useLeftRail ? "84px" : 0,
            pb: useLeftRail ? 0 : "76px",
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default PublicLayout;
